import { useTranslation } from 'react-i18next';

export type Omfang = '' | 'alle' | 'ingen' | 'noen';

/** Svaret på et policy-spørsmål mens kunden fyller ut. */
export interface Policy {
  omfang: Omfang;
  kjennetegn: string;
  kolonneILagerfil: string;
}

export function policyFraSvar(verdi: unknown): Policy {
  const v = typeof verdi === 'object' && verdi !== null && !Array.isArray(verdi) ? (verdi as Record<string, unknown>) : {};
  const omfang = ['alle', 'ingen', 'noen'].includes(v.omfang as string) ? (v.omfang as Omfang) : '';
  return {
    omfang,
    kjennetegn: typeof v.kjennetegn === 'string' ? v.kjennetegn : '',
    kolonneILagerfil: typeof v.kolonneILagerfil === 'string' ? v.kolonneILagerfil : '',
  };
}

/** Svaret som sendes. Kjennetegn og kolonne er bare med når svaret er «noen». */
export function policyTilSvar(p: Policy): Record<string, string> {
  if (p.omfang !== 'noen') return { omfang: p.omfang };
  const svar: Record<string, string> = { omfang: 'noen', kjennetegn: p.kjennetegn.trim() };
  if (p.kolonneILagerfil.trim()) svar.kolonneILagerfil = p.kolonneILagerfil.trim();
  return svar;
}

interface Props {
  id: string;
  policy: Policy;
  onEndre: (policy: Policy) => void;
}

/** Policy-spørsmål: gjelder egenskapen (for eksempel batch) alle, ingen eller noen av varene? */
export default function Policyfelt({ id, policy, onEndre }: Props) {
  const { t } = useTranslation();

  return (
    <fieldset aria-labelledby={`${id}-tekst`}>
      <div className="flex flex-col gap-2">
        {(['alle', 'ingen', 'noen'] as const).map((omfang) => (
          <label
            key={omfang}
            className={
              'valg ' + (policy.omfang === omfang ? 'valg-valgt' : '')
            }
          >
            <input
              type="radio"
              name={`${id}-omfang`}
              value={omfang}
              checked={policy.omfang === omfang}
              onChange={() => onEndre({ ...policy, omfang })}
              className="h-5 w-5 shrink-0"
            />
            <span>{t(`reise.policy.${omfang}`)}</span>
          </label>
        ))}
      </div>

      {policy.omfang === 'noen' && (
        <div className="mt-4 flex flex-col gap-4">
          <div>
            <label htmlFor={`${id}-kjennetegn`} className="mb-1.5 block text-sm font-medium text-slate-700">
              {t('reise.policy.kjennetegn')}
            </label>
            <p id={`${id}-kjennetegn-hjelp`} className="mb-1 text-sm text-slate-600">
              {t('reise.policy.kjennetegnHjelp')}
            </p>
            <textarea
              id={`${id}-kjennetegn`}
              rows={3}
              value={policy.kjennetegn}
              aria-describedby={`${id}-kjennetegn-hjelp`}
              onChange={(e) => onEndre({ ...policy, kjennetegn: e.target.value })}
              className="felt w-full"
            />
          </div>
          <div>
            <label htmlFor={`${id}-kolonne`} className="mb-1.5 block text-sm font-medium text-slate-700">
              {t('reise.policy.kolonne')}
            </label>
            <input
              id={`${id}-kolonne`}
              type="text"
              value={policy.kolonneILagerfil}
              onChange={(e) => onEndre({ ...policy, kolonneILagerfil: e.target.value })}
              className="felt w-full sm:w-80"
            />
          </div>
        </div>
      )}
    </fieldset>
  );
}
